/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, react-hooks/rules-of-hooks, no-undef */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { setClassNames } from 'ddc-classnames-js';
import { useRequestData } from 'ws-scripts/modules/common';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs } from 'wsm-common-data';
import { trackPerformanceToNR } from '../utilities/tracking';
import TemplateWrapper from '../components/TemplateWrapper';
import { usePromos } from '../hooks/usePromos';
import { usePrePromos } from '../hooks/usePrePromos';
import { getCouponStatus } from '../utilities/aspect';
import { useURLPrefs } from '../hooks/useURLPrefs';

const WidgetContainer = () => {
	const widgetRef = useRef();
	const [containerWidth, setContainerWidth] = useState(0);
	const { aspectRatio, enableCarousel } = useURLPrefs();
	const { deviceType, windowId } = useRequestData();
	const isMobile = deviceType === 'MOBILE';
	const isPreFetching =
		isBrowser &&
		window?.DDC?.WidgetData['ws-specials']?.[windowId]?.preloadedPromos;
	let promosState;
	let prePromos;

	if (isPreFetching) {
		[prePromos] = usePrePromos();
	} else {
		[promosState] = usePromos();
	}

	const { promos, isLoading, incentives } = isPreFetching
		? prePromos
		: promosState;

	let aspectRatioClass;
	const isSinglePromo = promos.length === 1;
	const isSingleCustomAssetWithDetails =
		isSinglePromo &&
		Object.keys(promos[0].customAssets).length !== 0 &&
		(promos[0]?.additionalDetails?.length > 0 ||
			promos[0]?.disclaimer?.length > 0);
	const isCoupon = getCouponStatus(aspectRatio);

	useEffect(() => {
		trackPerformanceToNR('ws-specials-starting');
		const perfEntries = global.performance.getEntriesByType('navigation');
		const PerformanceNavTiming = perfEntries[0];
		trackPerformanceToNR(
			'ws-specials-request-start',
			PerformanceNavTiming.requestStart
		);
		trackPerformanceToNR(
			'ws-specials-ttfb',
			PerformanceNavTiming.responseStart
		);
	}, []);

	// Set new widget containerWidth when parent element is resized, to preserve the aspect ratio
	useEffect(() => {
		if (isLoading) {
			return () => {};
		}
		const widgetWrapper = widgetRef.current.parentElement;
		let observer;

		// ResizeObserver is not polyfilled, so check for it first
		if (typeof ResizeObserver === 'function') {
			// Watch for parent element dimension change
			observer = new ResizeObserver((entry) => {
				setContainerWidth(entry[0].contentRect.width);
			});
			observer.observe(widgetWrapper);
		} else {
			// Set initial width here for browsers that do not support ResizeObserver
			setContainerWidth(widgetWrapper.getBoundingClientRect().width);
		}

		// Cleanup
		return () => observer && observer.unobserve(widgetWrapper);
	}, [widgetRef, isLoading]);

	const filteredPromos = useMemo(() => {
		let validPromos = [];
		if (promos && promos.length) {
			validPromos = promos;

			// Remove incentive promos that don't have matching incentive data
			if (incentives && incentives.length) {
				validPromos = promos.filter(
					(promo) =>
						!promo.incentiveId ||
						incentives.find(
							(incentive) =>
								!incentive.error &&
								promo.incentiveId === incentive.incentiveId
						)
				);
			}
		}
		return validPromos;
	}, [promos, incentives]);

	switch (aspectRatio) {
		case 'Mobile4x3_slideshow':
			aspectRatioClass = `aspect-ratio-mobile-4x3 coupon-listing slideshow ${
				!isSinglePromo && 'coupon-carousel'
			}`;
			break;
		case 'Mobile4x3_coupon':
			aspectRatioClass = `aspect-ratio-mobile-4x3 coupon-listing ${
				enableCarousel === 'true' && 'coupon-carousel'
			}`;
			break;
		case 'Mobile6x1':
			aspectRatioClass = 'aspect-ratio-mobile-6x1';
			break;
		case 'Desktop4x1':
			aspectRatioClass = 'aspect-ratio-desktop-4x1 overflow-hidden';
			break;
		case 'Desktop4x3':
			aspectRatioClass = `aspect-ratio-desktop-4x3 coupon-listing ${
				enableCarousel === 'true' && promos.length > 2
					? 'coupon-carousel mb-8'
					: ''
			}`;
			break;
		case 'Desktop10x1':
			aspectRatioClass = `aspect-ratio-desktop-10x1 ${
				!isSinglePromo || isSingleCustomAssetWithDetails ? 'mb-8' : ''
			}`;
			break;
		default:
			aspectRatioClass = 'aspect-ratio-desktop-4x1 overflow-hidden';
			break;
	}

	const containerClasses = [
		aspectRatioClass,
		!isLoading &&
		(!promos || promos.length === 0) &&
		(!incentives || incentives.length === 0)
			? 'd-none'
			: '',
		'position-relative'
	];

	const aspectRatioContainerClasses = ['w-100', 'bg-transparent'];

	const props = {
		aspectRatio,
		isMobile,
		width: containerWidth,
		isCoupon
	};

	return (
		<div ref={widgetRef} className={setClassNames(containerClasses)}>
			<div className={setClassNames(aspectRatioContainerClasses)}>
				{filteredPromos && filteredPromos.length ? (
					<TemplateWrapper promos={filteredPromos} {...props} />
				) : null}
			</div>
		</div>
	);
};

WidgetContainer.propTypes = {};

export default WidgetContainer;
